import type { OrderDetails, OrderGroupDetails } from '../model'
import type { DisplayState } from '@/entities/state/model'
import { formatEtaMinutes, formatScheduledTime } from './order.eta'

/**
 * Generates base status text for partner delivery orders
 * @param groupOrder - The order group to generate status text for
 * @param displayState - Optional display state for fallback text
 * @param options - Configuration options for text generation
 * @param options.includeApproxPrefix - Whether to include "Approx." prefix for scheduled times
 * @param options.fallbackText - Default fallback text if no other text is found
 * @returns Base status text string
 */
export function generateStatusText(
  groupOrder: OrderGroupDetails,
  displayState?: DisplayState,
  options: {
    includeApproxPrefix?: boolean // For compact view: "Approx. 2:30 PM" vs "2:30 PM"
    fallbackText?: string // Default fallback if no other text is found
  } = {},
): string {
  const { includeApproxPrefix = false, fallbackText = 'Partner Delivery' } = options

  const firstOrder = groupOrder.orders[0]
  const isOnTheWay = firstOrder && ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'].includes(firstOrder.status)

  // Check if any order in the group has an ETA and use the first match
  const orderWithEta = groupOrder.orders.find(order => order.eta && order.etaDirection)

  // Priority-based text generation using object literal
  const textGenerators = {
    // Priority 1: ETA for on-the-way orders
    etaForOnTheWay: () => isOnTheWay && orderWithEta
      ? formatEtaMinutes(orderWithEta.eta, orderWithEta.etaDirection)
      : null,

    // Priority 2: Scheduled time for on-the-way orders
    scheduledForOnTheWay: () => {
      if (!isOnTheWay || !firstOrder?.scheduledTimeForCollection)
        return null
      const scheduledTime = formatScheduledTime(firstOrder.scheduledTimeForCollection)
      if (!scheduledTime)
        return fallbackText
      return includeApproxPrefix ? `Approx. ${scheduledTime}` : scheduledTime
    },

    // Priority 3: Scheduled time from first order (fallback)
    scheduledFallback: () => formatScheduledTime(firstOrder?.scheduledTimeForCollection),

    // Priority 4: Display state information
    displayStateName: () => displayState?.stateDisplayName,
    displayCategory: () => displayState?.categoryTitle,

    // Priority 5: Order status
    orderStatus: () => firstOrder?.status,

    // Final fallback
    finalFallback: () => fallbackText,
  }

  // Execute generators in priority order and return first non-null result
  for (const generator of Object.values(textGenerators)) {
    const result = generator()
    if (result)
      return result
  }

  return fallbackText
}

/**
 * Formats driver text for individual order based on order status
 * @param order - The order to format text for
 * @param driverName - The driver's name
 * @returns Formatted text string or undefined if no driver name
 */
export function formatSingleDriverText(
  order: OrderDetails,
  driverName: string | undefined,
): string | undefined {
  if (!driverName)
    return undefined

  const isDelivering = order.status === 'DeliveringToCustomer'
  const isOnTheWay = ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'].includes(order.status)

  if (isDelivering) {
    return `Delivering to ${driverName}`
  }

  if (isOnTheWay) {
    return `On the way to ${driverName}`
  }

  return `Driver: ${driverName}`
}

/**
 * Formats driver text for partner delivery orders based on order status
 * @param groupOrder - The order group to format text for
 * @param driverName - The driver's name
 * @returns Formatted text string or undefined if no driver name
 */
export function formatDriverText(
  groupOrder: OrderGroupDetails,
  driverName: string | undefined,
): string | undefined {
  if (!driverName)
    return undefined

  const isDelivering = groupOrder.orders.some(order => order.status === 'DeliveringToCustomer')
  const isOnTheWay = groupOrder.orders.some(order =>
    ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'].includes(order.status),
  )

  if (isDelivering) {
    return `Delivering to ${driverName}`
  }

  if (isOnTheWay) {
    // Use shared status text generation for consistent ETA/scheduled time handling
    // For on-the-way orders, only use fallback if no display state is available
    const baseText = generateStatusText(groupOrder, undefined, { fallbackText: 'On The Way' })
    return `${baseText} to ${driverName}`
  }

  return `Driver: ${driverName}`
}

/**
 * Checks if driver name should be appended to status text based on order states
 * @param groupOrder - The order group to check
 * @returns true if driver name should be appended to status text
 */
export function shouldShowDriverInStatus(groupOrder: OrderGroupDetails): boolean {
  const isDelivering = groupOrder.orders.some(order => order.status === 'DeliveringToCustomer')
  const isOnTheWay = groupOrder.orders.some(order =>
    ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'].includes(order.status),
  )
  return isDelivering || isOnTheWay
}

/**
 * Merges realtime orders with polled orders, prioritizing realtime data
 * based on timestamps to ensure the most up-to-date information is used
 */
export function mergeRealtimeWithPolledOrders(
  polledOrders: OrderDetails[],
  realtimeOrders: OrderDetails[],
  lastRealtimeEventTime: string,
  lastPollTime: string,
): OrderDetails[] {
  // If no realtime orders or realtime is older than last poll, use polled orders
  if (!realtimeOrders.length || lastRealtimeEventTime < lastPollTime) {
    return polledOrders
  }

  const mergedMap = new Map<string, OrderDetails>()

  // Start with polled orders as base
  polledOrders.forEach((order) => {
    mergedMap.set(order.orderId, order)
  })

  // Overlay realtime orders, but only if they're newer
  realtimeOrders.forEach((realtimeOrder) => {
    const existing = mergedMap.get(realtimeOrder.orderId)

    if (!existing || realtimeOrder.lastUpdateTime >= existing.lastUpdateTime) {
      mergedMap.set(realtimeOrder.orderId, realtimeOrder)
    }
  })

  return Array.from(mergedMap.values())
}
